import { getCatalog } from "@/api/client";
import type { Product } from "@/types/api";

let catalogCache: Product[] | null = null;

export async function loadCatalogProducts() {
  if (catalogCache) {
    return catalogCache;
  }

  const data = await getCatalog();
  catalogCache = data.products;
  return data.products;
}

export function clearCatalogCache() {
  catalogCache = null;
}

export function getCertificateKindLabel(kind: Product["certificateKind"]) {
  switch (kind) {
    case "ECPF":
      return "e-CPF";
    case "ECNPJ":
      return "e-CNPJ";
    case "EMEI":
      return "e-MEI";
    default:
      return "";
  }
}

export function findCertificateProduct(
  products: Product[],
  kind: "ECPF" | "ECNPJ" | "EMEI",
  type: "A1" | "A3"
) {
  const matches = products.filter(
    (product) =>
      product.productType === "CERTIFICATE" &&
      product.certificateKind === kind &&
      product.certificateType === type
  );

  return matches.find((product) => product.metadata?.defaultSelection === true) ?? matches[0];
}

export function findTokenProduct(products: Product[]) {
  return products.find((product) => product.productType === "TOKEN");
}

/**
 * Checks whether a given SKU or product represents an ONLINE emission
 * (i.e. its metadata has `emissao: "online"` or the SKU ends with `-ON`).
 * These products require a recent CNH for biometric video-validation.
 */
export function isOnlineEmission(product: Product | undefined | null): boolean {
  if (!product) return false;
  if (product.productType !== "CERTIFICATE") return false;
  const metaEmissao = (product.metadata as { emissao?: string } | undefined)?.emissao;
  if (metaEmissao === "online") return true;
  if (metaEmissao === "presencial") return false;
  return /-ON$/i.test(product.sku);
}

/**
 * Given an ONLINE SKU (ending in `-ON`), returns the equivalent presencial
 * product (same cert kind + type, SKU suffix `-PRES`) loaded in the catalog,
 * or null if no match exists.
 */
export function findPresentialEquivalent(
  products: Product[],
  onlineProduct: Product
): Product | null {
  if (!/-ON$/i.test(onlineProduct.sku)) return null;
  const targetSku = onlineProduct.sku.replace(/-ON$/i, "-PRES");
  return products.find((p) => p.sku === targetSku) ?? null;
}
